import type { ChangedOccurrence } from '@/lib/spell-check/spell-check-state'
import type { SpellText } from '@/lib/spell-check/spell-engine'
import type { ShapeTextRangeEdit } from '@/lib/shapes/types/shape-text-range-edit'

/**
 * The text edits that write a Change or Change All: one group per instance, in the same order.
 * Each range carries the text the scan saw there, so ShapeManager refuses it if the text has moved
 * since. A word a linked chain cut is one group of two ranges: the replacement goes where the word
 * starts and its rest is taken off the start of the next frame. An instance whose text is not in
 * `texts` gets an empty group, which is never written.
 */
export function changeReplacements(
  changed: readonly ChangedOccurrence[],
  texts: readonly SpellText[]
): ShapeTextRangeEdit[][] {
  const textOf = (shapeId: string, onPath: boolean) =>
    texts.find((t) => t.shapeId === shapeId && (t.onPath ?? false) === onPath)?.text

  return changed.map(({ occurrence }) => {
    const { shapeId, start, end, replacement = '', continuation } = occurrence
    const onPath = occurrence.onPath === true

    if (shapeId === undefined || start === undefined || end === undefined) return []

    const text = textOf(shapeId, onPath)

    if (text === undefined) return []

    const head: ShapeTextRangeEdit = {
      shapeId,
      start,
      end,
      expected: text.slice(start, end),
      text: replacement,
      ...(onPath && { onPath: true }),
    }

    if (!continuation) return [head]

    const next = textOf(continuation.shapeId, false)

    if (next === undefined) return []

    return [
      head,
      {
        shapeId: continuation.shapeId,
        start: 0,
        end: continuation.length,
        expected: next.slice(0, continuation.length),
        text: '',
      },
    ]
  })
}
